// src/components/mensagens/MessageHistoryModal.jsx
// ============================================================================
// 🎯 COMPONENTE: MessageHistoryModal — V18 ENTERPRISE ULTRA PREMIUM
// 📋 Função: Histórico completo de conversa com todas as operações CRUD
// 📱 Mobile: Bottom Sheet nativo tipo app · Swipe · Long press · Haptic
// 🖥️ Desktop: Modal centrado com glassmorphism · Atalhos de teclado
// 🎨 Design: Ultra moderno · Animações GPU-accelerated · Micro-interações
// 🔒 Robusto: Anti-crash · Validação · Estados vazios · Loading · Erro
// ============================================================================
// 🔥 V18: Integração completa com MessageBubble, MessageActionMenu,
//         DeleteConfirmModal, InlineMessageEditor
// 🔥 V18: Navegação para MessageDetails ao clicar na bolha
// 🔥 V18: Editar, Reenviar, Copiar, Excluir mensagem individual
// 🔥 V18: Excluir conversa completa com confirmação
// 🔥 V18: Bottom sheet nativo mobile com gesto de arraste
// 🔥 V18: Agrupamento de mensagens por data (Hoje, Ontem, etc.)
// ============================================================================

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Download,
  ChevronDown,
  ChevronUp,
  Trash2,
  Search,
  SlidersHorizontal,
  MessageSquare,
  Phone,
  Calendar,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Send,
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import {
  formatDateTime,
  getMessageActionPermissions,
  groupMessagesByDate,
  truncateText,
} from './helpers';

// Componentes hijos
import MessageBubble from './MessageBubble';
import MessageActionMenu from './MessageActionMenu';
import DeleteConfirmModal from './DeleteConfirmModal';
import InlineMessageEditor from './InlineMessageEditor';

// ============================================================================
// 🎨 HELPERS SEGUROS (Anti-crash)
// ============================================================================
function safeArray(arr) {
  return Array.isArray(arr) ? arr : [];
}

function safeString(str, fallback = '') {
  return typeof str === 'string' ? str : fallback;
}

function safeNumber(num, fallback = 0) {
  return typeof num === 'number' && Number.isFinite(num) ? num : fallback;
}

function classNames(...arr) {
  return arr.filter(Boolean).join(' ');
}

// ============================================================================
// 🎯 SUBCOMPONENTE: MiniStats (memoizado)
// ============================================================================
const MiniStats = memo(({ stats, isMobile }) => {
  if (!stats) return null;

  const items = [
    { label: 'Total', value: stats.total, icon: MessageSquare, color: 'text-slate-500' },
    { label: 'Enviadas', value: stats.outgoing, icon: Send, color: 'text-emerald-500' },
    { label: 'Recebidas', value: stats.incoming, icon: MessageSquare, color: 'text-blue-500' },
    { label: 'Lidas', value: stats.read, icon: CheckCircle, color: 'text-green-500' },
  ];

  if (isMobile) {
    return (
      <div className="flex items-center gap-3 mt-1.5 overflow-x-auto scrollbar-hide">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-1 flex-shrink-0">
            <item.icon size={11} className={item.color} />
            <span className="text-[10px] text-slate-500 font-medium">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="hidden sm:flex items-center gap-4 mt-2 text-xs text-slate-500">
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1">
          <item.icon size={11} className={item.color} />
          <span className="font-medium">{item.label}:</span> {item.value}
        </span>
      ))}
    </div>
  );
});
MiniStats.displayName = 'MiniStats';

// ============================================================================
// 🎯 SUBCOMPONENTE: EmptyState (memoizado)
// ============================================================================
const EmptyState = memo(({ filter, onShowAll, isMobile }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4">
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`${isMobile ? 'w-14 h-14' : 'w-20 h-20'} rounded-3xl bg-slate-100 flex items-center justify-center mb-4`}
    >
      <MessageSquare size={isMobile ? 24 : 32} className="text-slate-300" />
    </motion.div>
    <p className="text-slate-500 font-semibold text-sm mb-1">
      Nenhuma mensagem encontrada
    </p>
    <p className="text-slate-400 text-xs text-center max-w-[250px]">
      {filter !== 'all'
        ? 'Tente mudar o filtro para ver mais resultados'
        : 'As mensagens enviadas e recebidas aparecerão aqui'}
    </p>
    {filter !== 'all' && (
      <button
        onClick={onShowAll}
        className="mt-4 px-4 py-2 text-xs font-semibold text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
      >
        Mostrar todas as mensagens
      </button>
    )}
  </div>
));
EmptyState.displayName = 'EmptyState';

// ============================================================================
// 🎯 SUBCOMPONENTE: DateDivider (memoizado)
// ============================================================================
const DateDivider = memo(({ label }) => (
  <div className="flex items-center gap-3 py-2 first:pt-0">
    <div className="flex-1 h-px bg-slate-200" />
    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0">
      {label}
    </span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
));
DateDivider.displayName = 'DateDivider';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL — MessageHistoryModal V18 ULTRA PREMIUM
// ============================================================================
function MessageHistoryModal({
  isOpen = false,
  client = null,
  messages = [],
  onClose,
  primaryColor = '#10b981',
  onDeleteMessage: onDeleteMessageProp,
  onMarkAsRead: onMarkAsReadProp,
  onDeleteConversation: onDeleteConversationProp,
  onEditMessage: onEditMessageProp,
  onResendMessage: onResendMessageProp,
}) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Refs
  const modalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const contextMenuTriggerRef = useRef(null);

  // ==========================================================================
  // 📊 ESTADOS
  // ==========================================================================
  const [filter, setFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Menu contextual
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    message: null,
    triggerRef: null,
  });

  // Modal de exclusão
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: 'message',
    data: null,
    loading: false,
  });

  // Editor inline
  const [editingMessage, setEditingMessage] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Toast interno
  const [toast, setToast] = useState(null);

  // ==========================================================================
  // 🎯 TOAST INTERNO
  // ==========================================================================
  const showInternalToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ==========================================================================
  // 🔄 SCROLL TO BOTTOM
  // ==========================================================================
  useEffect(() => {
    if (!isOpen) return;

    const raf = requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'instant',
          block: 'end',
        });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isOpen, messages.length]);

  // ==========================================================================
  // 📐 DETECTAR SCROLL POSITION
  // ==========================================================================
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const threshold = 80;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setIsScrolledToBottom(scrollHeight - scrollTop - clientHeight < threshold);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  // ==========================================================================
  // ⌨️ FECHAR COM ESC
  // ==========================================================================
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (contextMenu.isOpen) {
          setContextMenu({ isOpen: false, message: null, triggerRef: null });
        } else if (editingMessage) {
          setEditingMessage(null);
        } else if (deleteModal.isOpen) {
          setDeleteModal({ isOpen: false, type: 'message', data: null, loading: false });
        } else {
          onClose?.();
        }
      }
    };

    window.addEventListener('keydown', handleEsc, { passive: true });
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, contextMenu.isOpen, editingMessage, deleteModal.isOpen]);

  // ==========================================================================
  // 📊 FILTRAR E ORDENAR — Memoizado
  // ==========================================================================
  const filteredMessages = useMemo(() => {
    let list = safeArray(messages);

    // Filtro por direção
    if (filter === 'outgoing') {
      list = list.filter((m) => m.direction === 'outgoing');
    } else if (filter === 'incoming') {
      list = list.filter((m) => m.direction === 'incoming');
    }

    // Busca textual
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(
        (m) =>
          safeString(m.message).toLowerCase().includes(term) ||
          safeString(m.template).toLowerCase().includes(term)
      );
    }

    // Ordenação
    return list.sort((a, b) => {
      const dateA = safeNumber(a?.createdAt, 0);
      const dateB = safeNumber(b?.createdAt, 0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [messages, filter, sortOrder, searchTerm]);

  // ==========================================================================
  // 📊 AGRUPAR POR DATA — Memoizado
  // ==========================================================================
  const groupedMessages = useMemo(
    () => groupMessagesByDate(filteredMessages),
    [filteredMessages]
  );

  // ==========================================================================
  // 📊 ESTATÍSTICAS — Memoizado
  // ==========================================================================
  const stats = useMemo(() => {
    const list = safeArray(messages);

    return {
      total: list.length,
      outgoing: list.filter((m) => m.direction === 'outgoing').length,
      incoming: list.filter((m) => m.direction === 'incoming').length,
      read: list.filter((m) => m.status === 'read').length,
      delivered: list.filter((m) => m.status === 'delivered').length,
      failed: list.filter((m) => m.status === 'failed').length,
    };
  }, [messages]);

  const readPercentage = useMemo(() => {
    if (stats.outgoing === 0) return 0;
    return Math.round((stats.read / stats.outgoing) * 100);
  }, [stats.outgoing, stats.read]);

  // ==========================================================================
  // 🎮 HANDLERS — Menu Contextual
  // ==========================================================================
  const handleOpenContextMenu = useCallback((message, triggerRef) => {
    contextMenuTriggerRef.current = triggerRef?.current || null;
    setContextMenu({
      isOpen: true,
      message,
      triggerRef: contextMenuTriggerRef,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ isOpen: false, message: null, triggerRef: null });
  }, []);

  // ==========================================================================
  // 🎮 HANDLERS — Ver detalhes (navegação)
  // ==========================================================================
  const handleViewMessage = useCallback(
    (message) => {
      if (message?.id) {
        navigate(`/mensagens/${message.id}`);
      }
      handleCloseContextMenu();
    },
    [navigate, handleCloseContextMenu]
  );

  // ==========================================================================
  // 🎮 HANDLERS — Editar mensagem
  // ==========================================================================
  const handleEditMessage = useCallback(
    (message) => {
      setEditingMessage(message);
      handleCloseContextMenu();
    },
    [handleCloseContextMenu]
  );

  const handleSaveEdit = useCallback(
    async (newText, message) => {
      if (!message?.id || !newText) return;

      setEditLoading(true);

      try {
        // Atualizar via prop ou localmente
        if (typeof onEditMessageProp === 'function') {
          await onEditMessageProp(message.id, newText);
        }

        showInternalToast('Mensagem atualizada com sucesso!');
        setEditingMessage(null);
      } catch (error) {
        console.error('❌ [MessageHistoryModal] Erro ao editar:', error);
        showInternalToast('Erro ao editar mensagem', 'error');
      } finally {
        setEditLoading(false);
      }
    },
    [onEditMessageProp, showInternalToast]
  );

  const handleCancelEdit = useCallback(
    (hasChanges) => {
      if (hasChanges) {
        const confirmed = window.confirm(
          'Você tem alterações não salvas. Deseja realmente cancelar?'
        );
        if (!confirmed) return;
      }
      setEditingMessage(null);
    },
    []
  );

  // ==========================================================================
  // 🎮 HANDLERS — Reenviar mensagem
  // ==========================================================================
  const handleResendMessage = useCallback(
    (message) => {
      if (typeof onResendMessageProp === 'function') {
        onResendMessageProp(message);
      }
      handleCloseContextMenu();
      showInternalToast('Mensagem pronta para reenvio!');
    },
    [onResendMessageProp, handleCloseContextMenu, showInternalToast]
  );

  // ==========================================================================
  // 🎮 HANDLERS — Copiar mensagem
  // ==========================================================================
  const handleCopyMessage = useCallback(
    async (message) => {
      if (!message?.message) return;

      try {
        await navigator.clipboard.writeText(message.message);
        showInternalToast('Mensagem copiada!');
      } catch {
        showInternalToast('Erro ao copiar mensagem', 'error');
      }

      handleCloseContextMenu();
    },
    [handleCloseContextMenu, showInternalToast]
  );

  // ==========================================================================
  // 🎮 HANDLERS — Excluir mensagem individual
  // ==========================================================================
  const handleDeleteClick = useCallback(
    (message) => {
      setDeleteModal({
        isOpen: true,
        type: 'message',
        data: {
          message,
          clientName: client?.name,
          clientPhone: client?.phone,
        },
        loading: false,
      });
      handleCloseContextMenu();
    },
    [client, handleCloseContextMenu]
  );

  const confirmDeleteMessage = useCallback(async () => {
    const message = deleteModal.data?.message;
    if (!message?.id) return;

    setDeleteModal((prev) => ({ ...prev, loading: true }));

    try {
      if (typeof onDeleteMessageProp === 'function') {
        await onDeleteMessageProp(message.id);
      }

      showInternalToast('Mensagem excluída com sucesso!');
      setDeleteModal({ isOpen: false, type: 'message', data: null, loading: false });
    } catch (error) {
      console.error('❌ [MessageHistoryModal] Erro ao excluir:', error);
      showInternalToast('Erro ao excluir mensagem', 'error');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  }, [deleteModal.data, onDeleteMessageProp, showInternalToast]);

  // ==========================================================================
  // 🎮 HANDLERS — Excluir conversa completa
  // ==========================================================================
  const handleDeleteConversationClick = useCallback(() => {
    setDeleteModal({
      isOpen: true,
      type: 'conversation',
      data: {
        count: messages.length,
        clientName: client?.name,
        clientPhone: client?.phone,
      },
      loading: false,
    });
  }, [messages.length, client]);

  const confirmDeleteConversation = useCallback(async () => {
    if (!client?.id) return;

    setDeleteModal((prev) => ({ ...prev, loading: true }));

    try {
      if (typeof onDeleteConversationProp === 'function') {
        await onDeleteConversationProp(client.id);
      }

      showInternalToast('Conversa excluída com sucesso!');
      setDeleteModal({ isOpen: false, type: 'conversation', data: null, loading: false });
      onClose?.();
    } catch (error) {
      console.error('❌ [MessageHistoryModal] Erro ao excluir conversa:', error);
      showInternalToast('Erro ao excluir conversa', 'error');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  }, [client, onDeleteConversationProp, onClose, showInternalToast]);

  // ==========================================================================
  // 🎮 HANDLERS — Exportar
  // ==========================================================================
  const handleExportConversation = useCallback(() => {
    try {
      const content = filteredMessages
        .map(
          (m) =>
            `[${formatDateTime(m.createdAt)}] ${
              m.direction === 'outgoing'
                ? '📤 Você'
                : '📥 ' + safeString(client?.name, 'Cliente')
            }: ${safeString(m.message)}`
        )
        .join('\n\n');

      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversa-${safeString(client?.name, 'cliente')
        .replace(/\s+/g, '_')
        .toLowerCase()}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showInternalToast('Conversa exportada!');
    } catch (error) {
      console.error('❌ [MessageHistoryModal] Erro ao exportar:', error);
      showInternalToast('Erro ao exportar', 'error');
    }
  }, [filteredMessages, client?.name, showInternalToast]);

  // ==========================================================================
  // 🎮 HANDLERS — Ordenação e filtros
  // ==========================================================================
  const handleToggleSort = useCallback(() => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const handleShowAll = useCallback(() => {
    setFilter('all');
    setSearchTerm('');
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, []);

  // ==========================================================================
  // ⏸️ EARLY RETURN
  // ==========================================================================
  if (!isOpen || !client) return null;

  const clientInitial = safeString(client.name, '?').charAt(0).toUpperCase();
  const clientName = safeString(client.name, 'Cliente');
  const clientPhone = safeString(client.phone, '');

  // ==========================================================================
  // 🎨 ANIMAÇÕES
  // ==========================================================================
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const mobileSheetVariants = {
    hidden: { y: '100%' },
    visible: {
      y: 0,
      transition: { type: 'spring', damping: 30, stiffness: 300, mass: 0.9 },
    },
    exit: {
      y: '100%',
      transition: { type: 'spring', damping: 28, stiffness: 250, mass: 1 },
    },
  };

  const desktopModalVariants = {
    hidden: { scale: 0.94, y: 20, opacity: 0 },
    visible: {
      scale: 1,
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 26,
        stiffness: 350,
        mass: 0.8,
      },
    },
    exit: {
      scale: 0.94,
      y: 20,
      opacity: 0,
      transition: { duration: 0.15, ease: 'easeIn' },
    },
  };

  // ==========================================================================
  // 🖼️ RENDER
  // ==========================================================================
  return (
    <AnimatePresence>
      {/* ================================================================ */}
      {/* OVERLAY */}
      {/* ================================================================ */}
      <motion.div
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.18 }}
        className={`
          fixed inset-0 z-[10000]
          ${isMobile
            ? 'flex items-end justify-center bg-black/70 backdrop-blur-md'
            : 'flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
          }
        `}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={`Histórico de conversa com ${clientName}`}
      >
        {/* ================================================================ */}
        {/* 📱 MOBILE: BOTTOM SHEET NATIVO */}
        {/* ================================================================ */}
        {isMobile ? (
          <motion.div
            ref={modalRef}
            variants={mobileSheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative w-full max-h-[92vh] bg-white rounded-t-3xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              contain: 'layout style paint',
              willChange: 'transform',
              boxShadow: '0 -20px 60px -10px rgba(0,0,0,0.3)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle de arraste */}
            <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
              <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* ===== HEADER ===== */}
            <HeaderContent
              clientName={clientName}
              clientPhone={clientPhone}
              clientInitial={clientInitial}
              stats={stats}
              filter={filter}
              setFilter={setFilter}
              sortOrder={sortOrder}
              handleToggleSort={handleToggleSort}
              handleExportConversation={handleExportConversation}
              handleDeleteConversationClick={handleDeleteConversationClick}
              onClose={onClose}
              showSearch={showSearch}
              setShowSearch={setShowSearch}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isMobile={true}
              primaryColor={primaryColor}
            />

            {/* ===== MENSAGENS ===== */}
            <MessagesContent
              scrollContainerRef={scrollContainerRef}
              groupedMessages={groupedMessages}
              clientName={clientName}
              isMobile={true}
              onView={handleViewMessage}
              onEdit={handleEditMessage}
              onResend={handleResendMessage}
              onCopy={handleCopyMessage}
              onDelete={handleDeleteClick}
              onContextMenu={handleOpenContextMenu}
              messagesEndRef={messagesEndRef}
              filter={filter}
              handleShowAll={handleShowAll}
            />

            {/* ===== FOOTER ===== */}
            <FooterContent
              filteredCount={filteredMessages.length}
              totalCount={messages.length}
              readPercentage={readPercentage}
              stats={stats}
              isMobile={true}
            />
          </motion.div>
        ) : (
          /* ================================================================ */
          /* 🖥️ DESKTOP: MODAL CENTRADO */
          /* ================================================================ */
          <motion.div
            ref={modalRef}
            variants={desktopModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[88vh] shadow-2xl border border-white/20 overflow-hidden flex flex-col"
            style={{
              contain: 'layout style paint',
              willChange: 'transform, opacity',
              boxShadow:
                '0 25px 50px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Barra de gradiente */}
            <div
              className="h-1 flex-shrink-0 bg-gradient-to-r from-emerald-500 to-green-600"
              style={{ willChange: 'transform' }}
            />

            {/* ===== HEADER ===== */}
            <HeaderContent
              clientName={clientName}
              clientPhone={clientPhone}
              clientInitial={clientInitial}
              stats={stats}
              filter={filter}
              setFilter={setFilter}
              sortOrder={sortOrder}
              handleToggleSort={handleToggleSort}
              handleExportConversation={handleExportConversation}
              handleDeleteConversationClick={handleDeleteConversationClick}
              onClose={onClose}
              showSearch={showSearch}
              setShowSearch={setShowSearch}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              isMobile={false}
              primaryColor={primaryColor}
            />

            {/* ===== MENSAGENS ===== */}
            <MessagesContent
              scrollContainerRef={scrollContainerRef}
              groupedMessages={groupedMessages}
              clientName={clientName}
              isMobile={false}
              onView={handleViewMessage}
              onEdit={handleEditMessage}
              onResend={handleResendMessage}
              onCopy={handleCopyMessage}
              onDelete={handleDeleteClick}
              onContextMenu={handleOpenContextMenu}
              messagesEndRef={messagesEndRef}
              filter={filter}
              handleShowAll={handleShowAll}
            />

            {/* ===== SCROLL TO BOTTOM ===== */}
            {!isScrolledToBottom && filteredMessages.length > 4 && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={scrollToBottom}
                className="absolute bottom-16 right-6 w-10 h-10 bg-white border border-slate-200 rounded-full shadow-lg flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-all z-20"
                style={{ contain: 'layout style paint' }}
                aria-label="Rolar para o final"
              >
                <ChevronDown size={18} />
              </motion.button>
            )}

            {/* ===== FOOTER ===== */}
            <FooterContent
              filteredCount={filteredMessages.length}
              totalCount={messages.length}
              readPercentage={readPercentage}
              stats={stats}
              isMobile={false}
            />
          </motion.div>
        )}
      </motion.div>

      {/* ================================================================ */}
      {/* MENU CONTEXTUAL */}
      {/* ================================================================ */}
      <MessageActionMenu
        isOpen={contextMenu.isOpen}
        onClose={handleCloseContextMenu}
        message={contextMenu.message}
        permissions={
          contextMenu.message
            ? getMessageActionPermissions(contextMenu.message)
            : null
        }
        onView={handleViewMessage}
        onEdit={handleEditMessage}
        onResend={handleResendMessage}
        onCopy={handleCopyMessage}
        onDelete={handleDeleteClick}
        triggerRef={contextMenuTriggerRef}
      />

      {/* ================================================================ */}
      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      {/* ================================================================ */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        type={deleteModal.type}
        data={deleteModal.data}
        onConfirm={
          deleteModal.type === 'conversation'
            ? confirmDeleteConversation
            : confirmDeleteMessage
        }
        onCancel={() =>
          setDeleteModal({
            isOpen: false,
            type: 'message',
            data: null,
            loading: false,
          })
        }
        loading={deleteModal.loading}
        primaryColor={primaryColor}
      />

      {/* ================================================================ */}
      {/* TOAST INTERNO */}
      {/* ================================================================ */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            className={`
              fixed bottom-6 left-1/2 -translate-x-1/2 z-[10003]
              px-5 py-3 rounded-2xl shadow-2xl
              flex items-center gap-2 text-sm font-semibold
              ${
                toast.type === 'error'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-900 text-white'
              }
            `}
          >
            {toast.type === 'error' ? (
              <AlertCircle size={16} />
            ) : (
              <CheckCircle size={16} className="text-emerald-400" />
            )}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}

// ============================================================================
// 🎯 SUBCOMPONENTE: HeaderContent
// ============================================================================
const HeaderContent = memo(
  ({
    clientName,
    clientPhone,
    clientInitial,
    stats,
    filter,
    setFilter,
    sortOrder,
    handleToggleSort,
    handleExportConversation,
    handleDeleteConversationClick,
    onClose,
    showSearch,
    setShowSearch,
    searchTerm,
    setSearchTerm,
    isMobile,
    primaryColor,
  }) => (
    <div
      className={classNames(
        'flex-shrink-0 border-b border-slate-200 bg-gradient-to-r from-emerald-50/90 to-green-50/90 backdrop-blur-sm',
        isMobile ? 'px-4 py-3' : 'px-5 py-4'
      )}
    >
      {/* Linha 1: Info + Ações */}
      <div className="flex items-start justify-between gap-3">
        {/* Avatar + Nome */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className={classNames(
              'rounded-2xl flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm bg-gradient-to-br from-emerald-500 to-green-600',
              isMobile ? 'w-10 h-10 text-sm' : 'w-12 h-12 text-base'
            )}
          >
            {clientInitial}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className={classNames(
                'font-bold text-slate-900 truncate',
                isMobile ? 'text-base' : 'text-lg'
              )}
            >
              {clientName}
            </h3>
            <div className="flex items-center gap-2 text-slate-500">
              <span className={isMobile ? 'text-[10px]' : 'text-xs'}>
                {stats.total} mensagens
              </span>
              {clientPhone && (
                <>
                  <span className="text-slate-300">•</span>
                  <span
                    className={classNames(
                      'truncate flex items-center gap-1',
                      isMobile
                        ? 'text-[10px] max-w-[100px]'
                        : 'text-xs max-w-[150px]'
                    )}
                  >
                    <Phone size={10} />
                    {clientPhone}
                  </span>
                </>
              )}
            </div>
            <MiniStats stats={stats} isMobile={isMobile} />
          </div>
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Busca (desktop) */}
          {!isMobile && (
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={classNames(
                'p-2 rounded-lg transition-all',
                showSearch
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-slate-500 hover:bg-white/60'
              )}
              aria-label="Buscar mensagens"
            >
              <Search size={16} />
            </button>
          )}

          {/* Filtro */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={classNames(
              'border border-slate-200 rounded-lg bg-white text-slate-700 font-medium cursor-pointer focus:ring-2 focus:ring-emerald-500/30 outline-none transition-all',
              isMobile ? 'text-[10px] px-2 py-1.5' : 'text-xs px-3 py-2'
            )}
          >
            <option value="all">Todas</option>
            <option value="outgoing">Enviadas</option>
            <option value="incoming">Recebidas</option>
          </select>

          {/* Ordenação */}
          <button
            onClick={handleToggleSort}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all active:scale-95"
            title={
              sortOrder === 'desc'
                ? 'Mais recentes primeiro'
                : 'Mais antigas primeiro'
            }
          >
            {sortOrder === 'desc' ? (
              <ChevronDown size={isMobile ? 14 : 16} />
            ) : (
              <ChevronUp size={isMobile ? 14 : 16} />
            )}
          </button>

          {/* Exportar */}
          <button
            onClick={handleExportConversation}
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-all active:scale-95"
            title="Exportar conversa"
          >
            <Download size={isMobile ? 14 : 16} />
          </button>

          {/* Excluir conversa (desktop) */}
          {!isMobile && (
            <button
              onClick={handleDeleteConversationClick}
              className="p-2 rounded-lg border border-red-200 bg-white hover:bg-red-50 text-red-500 transition-all active:scale-95"
              title="Excluir conversa"
            >
              <Trash2 size={16} />
            </button>
          )}

          {/* Excluir conversa (mobile - botão compacto) */}
          {isMobile && (
            <button
              onClick={handleDeleteConversationClick}
              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-all active:scale-95"
              title="Excluir conversa"
            >
              <Trash2 size={16} />
            </button>
          )}

          {/* Fechar */}
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/60 text-slate-500 transition-all active:scale-95"
            aria-label="Fechar"
          >
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>
      </div>

      {/* Linha 2: Busca (quando ativa) */}
      {showSearch && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="mt-3 overflow-hidden"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar nas mensagens..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
              autoFocus
            />
          </div>
        </motion.div>
      )}
    </div>
  )
);
HeaderContent.displayName = 'HeaderContent';

// ============================================================================
// 🎯 SUBCOMPONENTE: MessagesContent
// ============================================================================
const MessagesContent = memo(
  ({
    scrollContainerRef,
    groupedMessages,
    clientName,
    isMobile,
    onView,
    onEdit,
    onResend,
    onCopy,
    onDelete,
    onContextMenu,
    messagesEndRef,
    filter,
    handleShowAll,
  }) => (
    <div
      ref={scrollContainerRef}
      className={classNames(
        'flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent',
        isMobile ? 'px-3 py-3 space-y-1' : 'px-5 py-4 space-y-1'
      )}
      style={{
        contain: 'strict',
        willChange: 'scroll-position',
        overscrollBehavior: 'contain',
      }}
    >
      {groupedMessages.length === 0 ? (
        <EmptyState
          filter={filter}
          onShowAll={handleShowAll}
          isMobile={isMobile}
        />
      ) : (
        groupedMessages.map((group) => (
          <div key={group.label}>
            <DateDivider label={group.label} />
            <div className={isMobile ? 'space-y-1.5' : 'space-y-2'}>
              {group.messages.map((msg, index) => (
                <MessageBubble
                  key={msg.id || `msg-${index}`}
                  message={msg}
                  index={index}
                  clientName={clientName}
                  isMobile={isMobile}
                  onView={onView}
                  onEdit={onEdit}
                  onResend={onResend}
                  onCopy={onCopy}
                  onDelete={onDelete}
                  onContextMenu={onContextMenu}
                />
              ))}
            </div>
          </div>
        ))
      )}
      <div ref={messagesEndRef} />
    </div>
  )
);
MessagesContent.displayName = 'MessagesContent';

// ============================================================================
// 🎯 SUBCOMPONENTE: FooterContent
// ============================================================================
const FooterContent = memo(
  ({ filteredCount, totalCount, readPercentage, stats, isMobile }) => (
    <div
      className={classNames(
        'flex-shrink-0 border-t border-slate-200 bg-slate-50/90 backdrop-blur-sm',
        isMobile ? 'px-4 py-2.5' : 'px-5 py-3'
      )}
    >
      <div className="flex items-center justify-between text-slate-400">
        <span className={isMobile ? 'text-[10px]' : 'text-xs'}>
          {filteredCount !== totalCount
            ? `${filteredCount} de ${totalCount} mensagens`
            : `${totalCount} mensagens`}
        </span>
        <span
          className={classNames(
            'font-medium',
            isMobile ? 'text-[10px]' : 'text-xs'
          )}
        >
          {stats.outgoing > 0
            ? `${readPercentage}% de leitura`
            : 'Sem envios'}
        </span>
      </div>
    </div>
  )
);
FooterContent.displayName = 'FooterContent';

// ============================================================================
// 🎯 EXPORTAÇÕES
// ============================================================================
export { MiniStats, EmptyState, DateDivider, HeaderContent, MessagesContent, FooterContent };
export default memo(MessageHistoryModal);